import { sum, values, omit, pickBy, pick } from 'lodash-es';
import { SearchResponse } from '@algolia/client-search';

import { ContractTypeValue } from 'ha/types/SearchFilters';

export default (
  { facets = {} }: SearchResponse,
  { facets: propertyFacet = {} }: SearchResponse,
  { facets: propertySizeFacet = {} }: SearchResponse,
) => {
  /* eslint-disable @typescript-eslint/naming-convention */
  const {
    facility_bedroom_furnished,
    utilities,
    couplesAllowed,
    facility_tenant_status,
    preferredGender,
    facility_registration_possible,
    contractType,
    facility_bathroom,
    facility_balcony_terrace,
    facility_garden,
    facility_kitchen,
    facility_animal_allowed,
    facility_parking,
    facility_wheelchair_accessible,
    facility_basement,
    facility_tv,
    facility_desk,
    facility_dishwasher,
    facility_washing_machine,
    facility_dryer,
    facility_ac,
    facility_heating,
  } = facets;
  /* eslint-enable @typescript-eslint/naming-convention */

  const { propertyType, apartmentBedroomCount } = propertyFacet;
  const { propertySize } = propertySizeFacet;

  return {
    furniture: {
      furnished: facility_bedroom_furnished?.yes || 0,
      unfurnished: facility_bedroom_furnished?.no || 0,
    },
    propertyType: {
      studio: propertyType?.STUDIO || 0,
      apartment: (propertyType?.APARTMENT || 0) + (propertyType?.HOUSE || 0),
      privateRoom: propertyType?.PRIVATE_ROOM || 0,
      sharedRoom: propertyType?.SHARED_ROOM || 0,
    },
    rooms: {
      one: apartmentBedroomCount?.['1'] || 0,
      two: apartmentBedroomCount?.['2'] || 0,
      three: apartmentBedroomCount?.['3'] || 0,
      overFour: sum(values(omit(apartmentBedroomCount, [0, 1, 2, 3]))),
    },
    bills: {
      included: utilities?.I || 0,
    },
    suitableFor: {
      couples: couplesAllowed?.yes || 0,
      students: sum(
        values(omit(facility_tenant_status, 'working professionals only')),
      ),
      workingProfessionals: sum(
        values(omit(facility_tenant_status, 'students only')),
      ),
    },
    gender: {
      male: sum(values(omit(preferredGender, 'female'))),
      female: sum(values(omit(preferredGender, 'male'))),
    },
    registration: {
      possible: facility_registration_possible?.yes || 0,
    },
    contractType: {
      [ContractTypeValue.Daily]: contractType?.[ContractTypeValue.Daily] ?? 0,
      [ContractTypeValue.Fortnightly]:
        contractType?.[ContractTypeValue.Fortnightly] ?? 0,
      [ContractTypeValue.Monthly]:
        (contractType?.[ContractTypeValue.Monthly] ?? 0) +
        (contractType?.[ContractTypeValue.Unknown] ?? 0),
    },
    propertySize: {
      over15: sum(values(pickBy(propertySize, (_, key) => Number(key) >= 15))),
      over30: sum(values(pickBy(propertySize, (_, key) => Number(key) >= 30))),
      over60: sum(values(pickBy(propertySize, (_, key) => Number(key) >= 60))),
      over90: sum(values(pickBy(propertySize, (_, key) => Number(key) >= 90))),
    },
    facilities: {
      privateBathroom: facility_bathroom?.private || 0,
      balconyTerrance: sum(
        values(pick(facility_balcony_terrace, ['private', 'shared'])),
      ),
      garden: sum(values(pick(facility_garden, ['private', 'shared']))),
      kitchen: sum(values(pick(facility_kitchen, ['private', 'shared']))),
      petsFriendly: facility_animal_allowed?.yes || 0,
      parking: sum(values(pick(facility_parking, ['private', 'shared']))),
      wheelchairAccessible: facility_wheelchair_accessible?.yes || 0,
      basement: sum(values(pick(facility_basement, ['private', 'shared']))),
    },
    amenities: {
      tv: facility_tv?.yes || 0,
      desk: facility_desk?.yes || 0,
      dishwasher: facility_dishwasher?.yes || 0,
      washingMachine: facility_washing_machine?.yes || 0,
      dryer: facility_dryer?.yes || 0,
      airConditioning: facility_ac?.yes || 0,
      heating: sum(values(omit(facility_heating, 'no'))),
    },
  };
};
